<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">
        {{ editing ? "Editar empresa" : "Nueva empresa" }}
      </h1>
    </div>
    <div>
      <b-card tag="article" no-body style="max-width: 900px" class="custom-card">
        <b-card-header header-tag="section" class="custom-card-header d-flex align-items-center">
          <h3>{{ editing ? "Datos de la empresa" : "Datos de la nueva empresa" }}</h3>
        </b-card-header>

        <b-card-body class="custom-card-body p-0">
          <b-tabs card v-model="activeTab" class="company-tabs">

            <!-- Tab 1: Datos generales -->
            <b-tab>
              <template #title>
                <i class="ri-building-line mr-1"></i>Datos generales
              </template>

              <form class="custom-form" @submit.prevent="save" novalidate>
                <section class="form-section mb-0">
                  <div class="row">
                    <div class="col-md-12">
                      <div class="form-group">
                        <label for="ruc">RUC *</label>
                        <input
                          id="ruc"
                          type="text"
                          maxlength="11"
                          v-model.trim="form.ruc"
                          autocomplete="off"
                          placeholder="Ingresar número de RUC"
                          class="custom-form-control"
                          :class="{
                            'custom-form-control-error': $v.form.ruc.$error || companyIdExists,
                          }"
                          :disabled="disabledField"
                        />
                        <FormError v-if="$v.form.ruc.$error && !$v.form.ruc.required" message="RUC es requerido" />
                        <FormError v-if="$v.form.ruc.$error && !$v.form.ruc.numeric" message="RUC debe tener un valor numérico" />
                        <FormError v-if="$v.form.ruc.$error && !$v.form.ruc.minLength" message="RUC debe tener 11 dígitos" />
                        <FormError v-if="companyIdExists" message="Ya existe una empresa registrada con el mismo RUC" />
                      </div>

                      <div class="form-group">
                        <label for="companyName">Razón social *</label>
                        <input
                          id="companyName"
                          type="text"
                          maxlength="200"
                          v-model="form.name"
                          autocomplete="off"
                          placeholder="Ingresar razón social"
                          class="custom-form-control"
                          :class="{ 'custom-form-control-error': $v.form.name.$error }"
                        />
                        <FormError v-if="$v.form.name.$error && !$v.form.name.required" message="Razón social es requerida" />
                      </div>

                      <div class="form-group">
                        <label for="address">Dirección fiscal *</label>
                        <input
                          id="address"
                          type="text"
                          v-model="form.address"
                          autocomplete="off"
                          placeholder="Ingresar dirección fiscal"
                          class="custom-form-control"
                          :class="{ 'custom-form-control-error': $v.form.address.$error }"
                        />
                        <FormError v-if="$v.form.address.$error && !$v.form.address.required" message="Dirección fiscal es requerida" />
                      </div>
                    </div>

                    <div class="col-md-12">
                      <div class="form-group">
                        <label for="legalRepresentative">Representante legal</label>
                        <input
                          id="legalRepresentative"
                          type="text"
                          v-model="form.legalRepresentative"
                          autocomplete="off"
                          placeholder="Representante legal"
                          class="custom-form-control"
                        />
                      </div>
                    </div>

                    <!-- Logo -->
                    <div class="col-md-12" v-if="editing">
                      <div class="form-group">
                        <label>Logo de la empresa</label>
                        <div class="d-flex align-items-start flex-wrap">
                          <div
                            v-if="logoSrc"
                            class="border rounded d-flex align-items-center justify-content-center mr-4 mb-3"
                            style="width:160px;min-width:160px;height:100px;overflow:hidden;background:#f9fafb"
                          >
                            <img :src="logoSrc" alt="Logo" style="max-width:100%;max-height:100%;object-fit:contain" />
                          </div>
                          <div class="flex-1">
                            <input
                              type="file"
                              accept="image/png,image/jpeg"
                              class="custom-form-control mb-1"
                              @change="onFilePick('logo', $event)"
                              ref="logoInput"
                            />
                            <small class="text-muted">
                              PNG o JPG. Tamaño recomendado: 200×80px. PNG con fondo transparente es ideal.
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="form-group-buttons text-right">
                      <router-link :to="{ name: 'company-list' }" class="button button-light">
                        {{ editing ? "Volver" : "Cancelar" }}
                      </router-link>
                      <button class="button button-primary" type="submit">Guardar</button>
                    </div>
                  </div>
                </section>
              </form>
            </b-tab>

            <!-- Tab 2: Firma y sello (solo disponible al editar) -->
            <b-tab :disabled="!editing">
              <template #title>
                <i class="ri-quill-pen-line mr-1"></i>Firma y sello
                <span v-if="!editing" class="ml-1 text-muted small">(guarda primero)</span>
              </template>

              <form class="custom-form" @submit.prevent="saveConfig" novalidate>
                <section class="form-section mb-0">

                  <!-- Firma -->
                  <div class="form-group">
                    <label>Firma del representante legal</label>
                    <div class="d-flex align-items-start flex-wrap">
                      <div
                        v-if="signatureSrc"
                        class="border rounded d-flex align-items-center justify-content-center mr-4 mb-3"
                        style="width:160px;min-width:160px;height:100px;overflow:hidden;background:#f9fafb"
                      >
                        <img :src="signatureSrc" alt="Firma" style="max-width:100%;max-height:100%;object-fit:contain" />
                      </div>
                      <div class="flex-1">
                        <input
                          type="file"
                          accept="image/png,image/jpeg"
                          class="custom-form-control mb-1"
                          @change="onFilePick('signature', $event)"
                          ref="signatureInput"
                        />
                        <small class="text-muted">
                          Escanee la firma en hoja blanca. Recorte solo la firma. PNG con fondo transparente para mejor resultado.
                        </small>
                      </div>
                    </div>
                  </div>

                  <div class="form-group">
                    <label>Cargo del representante</label>
                    <input
                      type="text"
                      v-model="configForm.representativeTitle"
                      placeholder="Ej: Gerente General"
                      class="custom-form-control"
                    />
                  </div>

                  <!-- Sello -->
                  <div class="form-group">
                    <label>Sello de la empresa</label>
                    <div class="d-flex align-items-start flex-wrap">
                      <div
                        v-if="sealSrc"
                        class="border rounded d-flex align-items-center justify-content-center mr-4 mb-3"
                        style="width:160px;min-width:160px;height:100px;overflow:hidden;background:#f9fafb"
                      >
                        <img :src="sealSrc" alt="Sello" style="max-width:100%;max-height:100%;object-fit:contain" />
                      </div>
                      <div class="flex-1">
                        <input
                          type="file"
                          accept="image/png,image/jpeg"
                          class="custom-form-control mb-1"
                          @change="onFilePick('seal', $event)"
                          ref="sealInput"
                        />
                        <small class="text-muted">Opcional. PNG con fondo transparente.</small>
                      </div>
                    </div>
                  </div>

                  <!-- Texto legal -->
                  <div class="form-group">
                    <label>Texto legal pie de boleta</label>
                    <textarea
                      v-model="configForm.payslipFooterText"
                      rows="3"
                      class="custom-form-control"
                      placeholder="Boleta de pago emitida al amparo del D.S. 001-98-TR y D.Leg. 1310..."
                    ></textarea>
                  </div>

                  <div class="form-group-buttons text-right">
                    <router-link :to="{ name: 'company-list' }" class="button button-light">
                      Volver
                    </router-link>
                    <button class="button button-primary" type="submit">Guardar</button>
                  </div>

                </section>
              </form>
            </b-tab>

          </b-tabs>
        </b-card-body>
      </b-card>
    </div>
  </div>
</template>

<script>
import { Alert, FormError } from "wize-admin";
import { CompanyService } from "@/core/services";
import { numeric, required, minLength } from "vuelidate/lib/validators";
import { mapGetters } from "vuex";

export default {
  components: { FormError },

  data() {
    return {
      loading:  true,
      editing:  false,
      activeTab: 0,

      form: {
        ruc:                null,
        name:               null,
        address:            null,
        legalRepresentative: null,
      },
      companyIdExists: false,

      configForm: {
        representativeTitle: null,
        payslipFooterText:   null,
      },

      // Archivos seleccionados (File objects)
      files: {
        logo:      null,
        signature: null,
        seal:      null,
      },

      // ObjectURLs temporales (al elegir un archivo nuevo)
      previews: {
        logo:      null,
        signature: null,
        seal:      null,
      },

      // URLs del servidor (cargadas al abrir empresa existente)
      serverUrls: {
        logo:      null,
        signature: null,
        seal:      null,
      },
    };
  },

  validations: {
    form: {
      ruc:     { required, numeric, minLength: minLength(11) },
      name:    { required },
      address: { required },
    },
  },

  watch: {
    "$route.params.id": function () {
      if (this.$route.params.id) this.load(this.$route.params.id);
    },
    "form.ruc": function (val) {
      if (val && val.length === 11 && !this.editing) {
        this.getQueryRuc();
      }
    },
  },

  computed: {
    ...mapGetters({ user: "auth/getUser" }),
    disabledField() {
      return !!this.$route.params.id;
    },
    logoSrc()      { return this.previews.logo      || this.serverUrls.logo      || null; },
    signatureSrc() { return this.previews.signature || this.serverUrls.signature || null; },
    sealSrc()      { return this.previews.seal      || this.serverUrls.seal      || null; },
  },

  methods: {
    onFilePick(type, event) {
      const file = event.target.files[0];
      if (!file) return;
      if (file.size > 2 * 1024 * 1024) {
        Alert.error("El archivo no debe superar 2MB");
        event.target.value = "";
        return;
      }
      this.files[type]    = file;
      this.previews[type] = URL.createObjectURL(file);
    },

    async loadImages(id, payload) {
      const types = ['logo', 'signature', 'seal'];
      const pathMap = { logo: payload.logoPath, signature: payload.signaturePath, seal: payload.sealPath };
      await Promise.all(
        types
          .filter(t => pathMap[t])
          .map(async t => {
            try {
              const blob = await CompanyService.getConfigImage(id, t);
              this.serverUrls[t] = URL.createObjectURL(blob);
            } catch (e) {
              // imagen no encontrada — ignorar
            }
          })
      );
    },

    async getQueryRuc() {
      try {
        this.companyIdExists = false;
        const resp = (await CompanyService.queryRuc(this.form.ruc)).payload;
        this.companyIdExists = resp.alreadyExists;
        if (!resp.alreadyExists) {
          this.form.name    = resp.name;
          this.form.address = resp.address;
        } else {
          this.form.name    = null;
          this.form.address = null;
        }
      } catch (ex) {
        Alert.error(ex.errors.message);
        console.error(ex);
      }
    },

    async save() {
      this.$v.$touch();
      if (this.$v.$error || this.companyIdExists) {
        this.$nextTick(() => {
          this.$smoothScroll({ scrollTo: document.querySelector(".form-error"), updateHistory: false, offset: -50 });
        });
        return;
      }

      try {
        this.$store.dispatch("app/loading", true);
        const data = { ...this.form };
        let response;
        if (!this.editing) response = await CompanyService.save(data);
        else               response = await CompanyService.update(this.$route.params.id, data);

        // Guardar logo si se seleccionó uno
        if (this.files.logo) {
          const companyId = this.editing ? this.$route.params.id : response.payload.id;
          const formData = new FormData();
          formData.append("logo", this.files.logo);
          await CompanyService.saveConfig(companyId, formData);
          this.files.logo = null;
        }

        Alert.success("Se almacenó la información correctamente");
        if (!this.editing) {
          this.$router.push({ name: "company-edit", params: { id: response.payload.id } });
          this.editing = true;
        }
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : "Error al guardar");
      } finally {
        this.$store.dispatch("app/loading", false);
      }
    },

    async saveConfig() {
      try {
        this.$store.dispatch("app/loading", true);
        const formData = new FormData();
        if (this.configForm.representativeTitle != null) {
          formData.append("representativeTitle", this.configForm.representativeTitle);
        }
        if (this.configForm.payslipFooterText != null) {
          formData.append("payslipFooterText", this.configForm.payslipFooterText);
        }
        if (this.files.signature) formData.append("signature", this.files.signature);
        if (this.files.seal)      formData.append("seal",      this.files.seal);

        await CompanyService.saveConfig(this.$route.params.id, formData);
        Alert.success("Configuración guardada correctamente");

        this.files.signature = null;
        this.files.seal      = null;
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : "Error al guardar la configuración");
      } finally {
        this.$store.dispatch("app/loading", false);
      }
    },

    async load(id) {
      try {
        this.$store.dispatch("app/loading", true);
        const payload = (await CompanyService.get(id)).payload;
        this.form = {
          ruc:                payload.ruc,
          name:               payload.name,
          address:            payload.address,
          legalRepresentative: payload.legalRepresentative,
        };
        this.configForm.representativeTitle = payload.representativeTitle || null;
        this.configForm.payslipFooterText   = payload.payslipFooterText   || null;
        this.editing = true;
        await this.loadImages(id, payload);
      } catch (e) {
        console.error(e);
        Alert.error(e.errors ? e.errors.message : "Error al cargar");
        this.$router.push({ name: "company-list" });
      } finally {
        this.$store.dispatch("app/loading", false);
        setTimeout(() => { this.loading = false; }, 1000);
      }
    },
  },

  async created() {
    if (this.$route.params.id) this.load(this.$route.params.id);
    else this.loading = false;
  },
};
</script>

<style lang="stylus" scoped>
.company-tabs
  ::v-deep .nav-tabs
    border-bottom: 1px solid #dee2e6
    flex-wrap: nowrap
    overflow-x: auto
    .nav-link
      white-space: nowrap
</style>
